/**
 * Notification utilities for managing user notifications
 */

import { db } from '../firebase';
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  updateDoc,
  doc,
  Timestamp,
  getDocs
} from 'firebase/firestore';
import logger from './logger';
import { auth } from '../firebase';
import { API_URL } from '../constants/constants';

export const NOTIFICATION_TYPES = {
  CASE_MANAGER_ASSIGNED: 'CASE_MANAGER_ASSIGNED',
  BEHAVIORAL_HEALTH_ASSIGNED: 'BEHAVIORAL_HEALTH_ASSIGNED',
  HOUSING_NAVIGATOR_ASSIGNED: 'HOUSING_NAVIGATOR_ASSIGNED',
  CLIENT_UPDATE: 'CLIENT_UPDATE',
  TASK_ASSIGNED: 'TASK_ASSIGNED',
  CLIENT_STATUS_CHANGED: 'CLIENT_STATUS_CHANGED',
  CLIENT_EXITED: 'CLIENT_EXITED',
  CLIENT_BANNED: 'CLIENT_BANNED',
  CLIENT_HOUSED: 'CLIENT_HOUSED',
  CLIENT_TRANSFERRED: 'CLIENT_TRANSFERRED',
  CLIENT_REACTIVATED: 'CLIENT_REACTIVATED',
  NEW_CLIENT_ENROLLED: 'NEW_CLIENT_ENROLLED',
  IMPORTANT_FIELD_UPDATED: 'IMPORTANT_FIELD_UPDATED',
  TRAINING_ASSIGNED: 'TRAINING_ASSIGNED',
  TRAINING_DEADLINE_REMINDER: 'TRAINING_DEADLINE_REMINDER',
  RENT_LATE: 'RENT_LATE'
};

/**
 * Create a notification
 * @param {string} userId - The user ID to notify
 * @param {string} type - Notification type
 * @param {string} title - Notification title
 * @param {string} message - Notification message
 * @param {object} metadata - Additional metadata (clientId, etc.)
 * @returns {Promise<string>} Notification document ID
 */
export const createNotification = async (userId, type, title, message, metadata = {}) => {
  try {
    const notificationData = {
      userId,
      type,
      title,
      message,
      read: false,
      createdAt: Timestamp.now(),
      ...metadata
    };

    const docRef = await addDoc(collection(db, 'notifications'), notificationData);
    logger.debug('Notification created:', { id: docRef.id, ...notificationData });
    return docRef.id;
  } catch (error) {
    logger.error('Error creating notification:', error);
    throw error;
  }
};

/**
 * Create a case manager assignment notification
 * @param {string} caseManagerId - The case manager's user ID
 * @param {string} clientId - The client ID
 * @param {string} clientName - The client's name
 * @param {string} assignedByName - Name of person who made the assignment
 * @returns {Promise<string>} Notification document ID
 */
export const createCaseManagerAssignmentNotification = async (
  caseManagerId,
  clientId,
  clientName,
  assignedByName
) => {
  return createNotification(
    caseManagerId,
    NOTIFICATION_TYPES.CASE_MANAGER_ASSIGNED,
    'New Case Management Assignment',
    `You have been assigned as case manager for ${clientName}`,
    {
      clientId,
      clientName,
      assignedByName,
      actionUrl: `/client/${clientId}`
    }
  );
};

/**
 * Create an MHOP assignment notification
 * @param {string} staffId - The MHOP staff member's user ID
 * @param {string} clientId - The client ID
 * @param {string} clientName - The client's name
 * @param {string} assignedByName - Name of person who made the assignment
 * @returns {Promise<string>} Notification document ID
 */
export const createBehavioralHealthAssignmentNotification = async (
  staffId,
  clientId,
  clientName,
  assignedByName
) => {
  return createNotification(
    staffId,
    NOTIFICATION_TYPES.BEHAVIORAL_HEALTH_ASSIGNED,
    'New MHOP Assignment',
    `You have been assigned to ${clientName} for MHOP`,
    {
      clientId,
      clientName,
      assignedByName,
      actionUrl: `/client/${clientId}`
    }
  );
};

/**
 * Mark a notification as read
 * @param {string} notificationId - Notification document ID
 * @returns {Promise<void>}
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      read: true,
      readAt: Timestamp.now()
    });
  } catch (error) {
    logger.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Mark all notifications as read for a user
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export const markAllNotificationsAsRead = async (userId) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('read', '==', false)
    );

    const snapshot = await getDocs(q);
    const updatePromises = snapshot.docs.map(docRef =>
      updateDoc(docRef.ref, {
        read: true,
        readAt: Timestamp.now()
      })
    );

    await Promise.all(updatePromises);
    logger.debug(`Marked ${updatePromises.length} notifications as read for user ${userId}`);
  } catch (error) {
    logger.error('Error marking all notifications as read:', error);
    throw error;
  }
};

/**
 * Get unread notification count for a user
 * @param {string} userId - User ID
 * @param {function} callback - Callback function to receive count updates
 * @returns {function} Unsubscribe function
 */
export const subscribeToUnreadCount = (userId, callback) => {
  const q = query(
    collection(db, 'notifications'),
    where('userId', '==', userId),
    where('read', '==', false),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const count = snapshot.size;
      callback(count);
    },
    (error) => {
      logger.error('Error subscribing to unread count:', error);
      // Dispatch permission error event to prevent false logout
      if (error.code === 'permission-denied' || error.message?.includes('permission') || error.message?.includes('Missing or insufficient permissions')) {
        window.dispatchEvent(new CustomEvent('permission-error', {
          detail: { timestamp: Date.now(), source: 'notifications-unread-count' }
        }));
      }
      callback(0);
    }
  );
};

/**
 * Subscribe to notifications for a user
 * @param {string} userId - User ID
 * @param {function} callback - Callback function to receive notifications
 * @param {number} maxNotifications - Maximum number of notifications to fetch (default: 50)
 * @returns {function} Unsubscribe function
 */
export const subscribeToNotifications = (userId, callback, maxNotifications = 50) => {
  const q = query(
    collection(db, 'notifications'),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(maxNotifications)
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const notifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));
      callback(notifications);
    },
    (error) => {
      logger.error('Error subscribing to notifications:', error);
      // Dispatch permission error event to prevent false logout
      if (error.code === 'permission-denied' || error.message?.includes('permission') || error.message?.includes('Missing or insufficient permissions')) {
        window.dispatchEvent(new CustomEvent('permission-error', {
          detail: { timestamp: Date.now(), source: 'notifications-subscribe' }
        }));
      }
      callback([]);
    }
  );
};

/**
 * Delete a notification
 * @param {string} notificationId - Notification document ID
 * @returns {Promise<void>}
 */
export const deleteNotification = async (notificationId) => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, { deleted: true, deletedAt: Timestamp.now() });
  } catch (error) {
    logger.error('Error deleting notification:', error);
    throw error;
  }
};

/**
 * Create notification for important field updates (housedDate, referralDate, etc.)
 * @param {string} userId - User ID to notify (case manager or housing navigator)
 * @param {string} clientId - Client ID
 * @param {string} clientName - Client name
 * @param {string} fieldName - Field that was updated
 * @param {any} newValue - New value of the field
 * @param {string} performedByName - Name of person who made the update
 * @returns {Promise<string>} Notification document ID
 */
export const createImportantFieldUpdateNotification = async (
  userId,
  clientId,
  clientName,
  fieldName,
  newValue,
  performedByName
) => {
  const fieldLabels = {
    housedDate: 'Housed Date',
    referralDate: 'Referral Date',
    enrollmentDate: 'Enrollment Date',
    exitDate: 'Exit Date',
    firstReachOutDay: 'First Reach Out Day'
  };

  const fieldLabel = fieldLabels[fieldName] || fieldName;
  const valueDisplay = newValue instanceof Date
    ? newValue.toLocaleDateString()
    : newValue instanceof Timestamp
      ? newValue.toDate().toLocaleDateString()
      : newValue;

  return createNotification(
    userId,
    NOTIFICATION_TYPES.IMPORTANT_FIELD_UPDATED,
    'Important Field Updated',
    `${fieldLabel} for ${clientName} has been updated to ${valueDisplay}`,
    {
      clientId,
      clientName,
      fieldName,
      newValue: valueDisplay,
      performedByName: performedByName || 'Staff Member',
      actionUrl: `/client/${clientId}`
    }
  );
};

/**
 * Fetch unread Gmail inbox count for current user
 * @returns {Promise<{unreadCount:number, source:string, refreshedAt:string}>}
 */
export const fetchGmailUnreadCount = async () => {
  try {
    if (!auth.currentUser) {
      return { unreadCount: 0, source: 'gmail', refreshedAt: new Date().toISOString() };
    }

    const idToken = await auth.currentUser.getIdToken();
    const response = await fetch(`${API_URL}/api/users/me/gmail-unread-count`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const details = await response.text();
      logger.warn('Failed to fetch Gmail unread count', {
        status: response.status,
        details
      });
      return { unreadCount: 0, source: 'gmail', refreshedAt: new Date().toISOString() };
    }

    const payload = await response.json();
    return {
      unreadCount: Number.isFinite(payload.unreadCount) ? payload.unreadCount : 0,
      source: 'gmail',
      refreshedAt: payload.refreshedAt || new Date().toISOString()
    };
  } catch (error) {
    logger.warn('Error while fetching Gmail unread count', { error: error.message });
    return { unreadCount: 0, source: 'gmail', refreshedAt: new Date().toISOString() };
  }
};

